import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  ControlledTreeEnvironment,
  InteractionMode,
  Tree,
  type TreeItemIndex,
  type TreeRef,
} from 'react-complex-tree'
import { useNavigate } from 'react-router'

import { PostType, Space } from '@bettermode/common/gql/generated'
import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { ModelSettings } from '@tribeplatform/react-components/CMS'
import { useFirstRenderContext } from '@tribeplatform/react-components/common/ssr'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { cn } from '@tribeplatform/react-components/utils/cn'
import { useRouterPath } from '@tribeplatform/react-sdk'
import { SimpleDivider } from '@tribeplatform/react-ui-kit/Divider'
import { useSearchQuery } from '@tribeplatform/react-ui-kit/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Input } from '@tribeplatform/react-ui-kit/Input'
import { NavVertical } from '@tribeplatform/react-ui-kit/Sidebar'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { useAdminSidebar } from '../AdminSidebarContext.js'
import { AddCollection } from '../SiteTree/components/AddCollection.js'
import { AddSpace } from '../SiteTree/components/AddSpace.js'
import { TreeContainer } from '../SiteTree/components/TreeContainer.js'
import { TreeItemArrow } from '../SiteTree/components/TreeItemArrow.js'
import { TreeItemComponent } from '../SiteTree/components/TreeItemComponent.js'
import { TreeItemsContainer } from '../SiteTree/components/TreeItemsContainer.js'
import { TreeLiveDescriptorContainer } from '../SiteTree/components/TreeLiveDescriptorContainer.js'
import { CollectionMenuProvider } from '../SiteTree/contexts/CollectionMenuContext.js'
import {
  SiteTreeContextType,
  SiteTreeProvider,
} from '../SiteTree/contexts/SiteTreeContext.js'
import { useActiveSpaceData } from '../SiteTree/hooks/useActiveSpaceTreeItem.js'
import { useCMSTreeItems } from '../SiteTree/hooks/useCMSTreeItems.js'
import { useCollectionTreeItems } from '../SiteTree/hooks/useCollectionTreeItems.js'
import { useScrollToTreeItem } from '../SiteTree/hooks/useScrollToTreeItem.js'
import { useTreeViewState } from '../SiteTree/hooks/useTreeViewState.js'
import {
  COLLECTION_INDEX_PREFIX,
  collectionTreeItemIndex,
} from '../SiteTree/tree-items/createCollectionTreeItem.js'
import { NO_COLLECTION_INDEX } from '../SiteTree/tree-items/createNoCollectionTreeItem.js'
import {
  createRootTreeItem,
  ROOT_INDEX,
} from '../SiteTree/tree-items/createRootTreeItem.js'
import { TreeItems } from '../SiteTree/types.js'

const TREE_ID = 'site-tree'

const TREE_SETTINGS: SiteTreeContextType = {
  collection: {
    showMenu: true,
  },
  space: {
    to: (space: Space) => adminRoutes.site.spaces.general(space.id),
  },
  cms: {
    to: (postType: PostType) => adminRoutes.site.cms.settings(postType.slug),
    showMenu: true,
  },
}

interface TreeContentHandle {
  highlightCollection: (collectionId: string) => void
}

interface TreeContentProps {
  query?: string
  onItemSelect?: () => void
}

export function SiteRouteSidebar() {
  const { $t } = useI18n()
  const { query, inputProps } = useSearchQuery()
  const pathname = useRouterPath()
  const navigate = useNavigate()
  const { setSidebarOpen } = useAdminSidebar()
  const treeContentRef = useRef<TreeContentHandle>(null)
  const handleItemSelect = useCallback(
    () => setSidebarOpen(false),
    [setSidebarOpen],
  )
  /**
   * This component is registered in [sidebars.tsx](packages/web/src/client/components/Admin/AdminMenu/sidebars.tsx#L108)
   * as the secondary sidebar for the `/manage/site` route.
   * Handles responsive behavior for opening/closing on small devices where
   * the secondary sidebar is opened and closed by hamburger menu.
   */
  const isCmsPage = pathname.startsWith(`${adminRoutes.site.root}/cms`)

  return (
    <CollectionMenuProvider>
      <div className={cn('hidden', isCmsPage && 'flex')}>
        <ModelSettings
          isNew={pathname === adminRoutes.site.cms.new}
          goBack={() => navigate(adminRoutes.site.root)}
          onCreate={postType =>
            navigate(`${adminRoutes.site.cms.settings(postType.slug)}#fields`)
          }
        />
      </div>
      <div className={cn('h-full flex-col gap-4 hidden', !isCmsPage && 'flex')}>
        <div className="space-y-4">
          <Text variant="headingXs">
            <T id="Generics.Site" defaultMessage="Site" />
          </Text>
          <Input
            {...inputProps}
            id="search-tree-input"
            name="search-tree-input"
            placeholder={$t({
              id: 'Generics.Search',
              defaultMessage: 'Search',
            })}
          />
        </div>
        <div className="flex-1 overflow-hidden min-w-0">
          <div
            className="h-full overflow-y-auto overflow-x-hidden"
            style={{ scrollbarColor: '#939393 transparent' }}
          >
            <TreeContent
              ref={treeContentRef}
              query={query}
              onItemSelect={handleItemSelect}
            />
          </div>
        </div>
        <NavVertical className="space-y-2">
          <SimpleDivider className="my-1" variant="subdued" />
          <AddSpace to={adminRoutes.design.createSpace()} relative="path" />
          <AddCollection
            onSuccess={collection =>
              treeContentRef.current?.highlightCollection(collection.id)
            }
          />
        </NavVertical>
      </div>
    </CollectionMenuProvider>
  )
}

const TreeContent = forwardRef<TreeContentHandle, TreeContentProps>(
  ({ query, onItemSelect }, ref) => {
    const treeRef = useRef<TreeRef>(null)
    const [highlightedCollectionId, setHighlightedCollectionId] = useState<
      string | null
    >(null)

    const activeSpaceData = useActiveSpaceData()
    const activeItem = activeSpaceData?.treeIndex
    const scrolledToSpaceRef = useRef<TreeItemIndex | null>(null)

    const highlightedIndex = highlightedCollectionId
      ? collectionTreeItemIndex(highlightedCollectionId)
      : undefined

    const { items, expandedItems } = useSiteTreeItems({
      query,
      highlightedCollection: highlightedIndex,
      activeSpaceData,
    })

    const autoExpand = useMemo(() => {
      const expand = [...expandedItems]
      if (activeSpaceData?.collectionId) {
        expand.push(collectionTreeItemIndex(activeSpaceData.collectionId))
      }
      return expand
    }, [expandedItems, activeSpaceData?.collectionId])

    const { viewState, ...viewStateSetters } = useTreeViewState({
      initialExpanded: expandedItems,
      activeItem,
      autoExpand,
    })
    const { isFirstRender } = useFirstRenderContext()

    const clearHighlight = useCallback(() => {
      setHighlightedCollectionId(null)
    }, [])

    const onSpaceScrolled = useCallback(() => {
      scrolledToSpaceRef.current = activeItem ?? null
    }, [activeItem])

    useImperativeHandle(ref, () => ({
      highlightCollection: setHighlightedCollectionId,
    }))

    useScrollToTreeItem(treeRef, highlightedIndex, items, clearHighlight)

    // Scroll to the active space once it appears in the tree
    // (e.g. after its collection is expanded and spaces are loaded).
    const needsSpaceScroll =
      activeItem && scrolledToSpaceRef.current !== activeItem
        ? activeItem
        : undefined
    useScrollToTreeItem(treeRef, needsSpaceScroll, items, onSpaceScrolled)

    const treeContextValue: SiteTreeContextType = useMemo(
      () => ({
        ...TREE_SETTINGS,
        activeSpaceTreeIndex: activeItem,
        activeSpaceCollectionId: activeSpaceData?.collectionId,
        onItemSelect,
      }),
      [activeItem, activeSpaceData?.collectionId, onItemSelect],
    )

    if (isFirstRender) {
      return (
        <div className="flex items-center justify-center">
          <SvgIcon name="spinner" className="animate-spin" />
        </div>
      )
    }

    return (
      <SiteTreeProvider value={treeContextValue}>
        <ControlledTreeEnvironment
          {...viewStateSetters}
          viewState={{
            [TREE_ID]: viewState,
          }}
          items={items}
          getItemTitle={item => item.data.title}
          defaultInteractionMode={InteractionMode.ClickItemToExpand}
          renderItem={TreeItemComponent}
          renderItemArrow={TreeItemArrow}
          renderTreeContainer={TreeContainer}
          renderItemsContainer={TreeItemsContainer}
          renderLiveDescriptorContainer={TreeLiveDescriptorContainer}
        >
          <Tree
            ref={treeRef}
            treeId={TREE_ID}
            rootItem={ROOT_INDEX}
            treeLabel="Site"
          />
        </ControlledTreeEnvironment>
      </SiteTreeProvider>
    )
  },
)

function useSiteTreeItems({
  query,
  highlightedCollection,
  activeSpaceData,
}: {
  query?: string
  highlightedCollection?: TreeItemIndex
  activeSpaceData?: { treeIndex: TreeItemIndex; collectionId?: string }
}) {
  const activeCollectionIndex =
    highlightedCollection ??
    (activeSpaceData?.collectionId
      ? collectionTreeItemIndex(activeSpaceData.collectionId)
      : undefined)

  const { collectionTreeItems, collectionIndex } = useCollectionTreeItems({
    query,
    activeCollectionIndex,
    activeSpaceIndex: !activeSpaceData?.collectionId
      ? activeSpaceData?.treeIndex
      : undefined,
  })

  const { cmsTreeItems, cmsIndex } = useCMSTreeItems({
    query,
  })

  const items: TreeItems = useMemo(
    () => ({
      root: createRootTreeItem({
        children: [collectionIndex, cmsIndex],
      }),
      ...collectionTreeItems,

      ...cmsTreeItems,
    }),
    [collectionTreeItems, cmsTreeItems, collectionIndex, cmsIndex],
  )

  const expandedItems = useMemo(() => {
    const expanded: TreeItemIndex[] = [collectionIndex]
    if (query) {
      for (const key of Object.keys(collectionTreeItems)) {
        if (
          key.startsWith(COLLECTION_INDEX_PREFIX) ||
          key === NO_COLLECTION_INDEX
        ) {
          expanded.push(key)
        }
      }
    }
    return expanded
  }, [query, collectionTreeItems, collectionIndex])

  return { items, expandedItems }
}
